/**
 * @file AuthContext.jsx
 * @description Zentraler Authentifizierungs- und Berechtigungskontext für ThekenOS.
 *
 * Was dieser Kontext bereitstellt:
 * - Aktueller Login-Zustand des Benutzers (`user`)
 * - Zugewiesene Systemrolle (`role`: 'Theki', 'Beta-Theki', 'Redakteur', 'Admin', 'System-Admin', 'Einkäufer')
 * - Zusatzberechtigungen (`permissions`, z. B. Einkaufsrechte `hasEinkauf`)
 * - Automatischer Logout nach 10 Minuten Inaktivität zum Schutz offener Theken-Terminals
 * - Saubere Ladezustände (`loading`, `roleLoading`), um Flackern und Fehlumleitungen zu verhindern
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { loginWithUsername } from '../services/authService';

const AuthContext = createContext({});

/**
 * Globaler Provider für Authentifizierung und Benutzerrechte.
 * Umhüllt die gesamte Anwendung, sodass jede Komponente über `useAuth()` auf den Login-Status zugreifen kann.
 *
 * @param {object} props - Komponenten-Properties
 * @param {React.ReactNode} props.children - Verschachtelte Kind-Komponenten
 * @returns {JSX.Element} Provider-Element mit Authentifizierungsdaten
 */
export const AuthProvider = ({ children }) => {
  // Der aktuell angemeldete Supabase-Benutzer (oder null, wenn abgemeldet)
  const [user, setUser] = useState(null);
  // Die zugewiesene Systemrolle (z. B. 'Theki', 'Beta-Theki', 'Redakteur', 'Admin', 'System-Admin', 'Einkäufer')
  const [role, setRole] = useState(null);
  // Spezifische Zusatzberechtigungen (z. B. ob eine Thekenkraft auch die Einkaufsliste verwalten darf)
  const [permissions, setPermissions] = useState({ hasEinkauf: false });
  // Initialer Ladezustand beim allerersten Starten der App im Browser
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Wichtig: Unterscheidet zwischen "Rolle wird gerade noch aus der DB geladen" und "Benutzer hat keine Rolle".
  // Verhindert, dass geschützte Routen (PrivateRoute) während des Ladens fälschlicherweise "Zugriff verweigert" anzeigen.
  const [roleLoading, setRoleLoading] = useState(true);
  // Zeigt einen Hilfetext an, falls das Laden im Mobilfunknetz ungewöhnlich lange dauert (> 2,5 Sekunden)
  const [loadingTooLong, setLoadingTooLong] = useState(false);

  /**
   * Lädt die Rolle und Sonderberechtigungen eines Benutzers aus der Datenbank.
   *
   * Architektur-Hinweis:
   * Dieser Abruf läuft bewusst asynchron außerhalb des `onAuthStateChange`-Callbacks,
   * um Deadlocks mit dem internen Verbindungs-Lock von Supabase zu verhindern.
   *
   * @param {string} userId - Die UUID des Benutzers
   * @param {Function} [isStale] - Prüffunktion, ob eine neuere Anfrage gestartet wurde (verhindert Race Conditions)
   */
  async function fetchRole(userId, isStale = () => false) {
    let fetchedRole = null;
    try {
      // 1. Rolle über die Datenbankfunktion get_user_role abfragen
      const { data } = await supabase
        .rpc('get_user_role', { p_user_id: userId });

      if (isStale()) return;

      if (data) {
        // Rückgabe kann entweder ein Objekt `{ role: '...' }` oder direkt ein String sein
        fetchedRole = typeof data === 'object' ? data.role : data;
        setRole(fetchedRole);
      }
    } catch (err) {
      console.error("Fehler beim Abrufen der Benutzer-Rolle:", err);
    }

    if (isStale()) return;

    // 2. Sonderberechtigungen (z. B. Einkaufsrechte) abrufen
    try {
      const { data: permData } = await supabase
        .rpc('get_user_permissions', { p_user_id: userId });
      if (permData) {
        const parsed = typeof permData === 'string' ? JSON.parse(permData) : permData;
        setPermissions({
          hasEinkauf: parsed?.has_einkauf === true,
        });
      }
    } catch (err) {
      // Fallback: Ältere Datenbankversionen kennen die Funktion evtl. noch nicht
      console.warn('get_user_permissions nicht verfügbar, Fallback:', err?.message);
      setPermissions({ hasEinkauf: false });
    }

    if (isStale()) return;

    setInitialLoading(false);
    // Rolle ist geladen -> Navigation für den Nutzer sofort freigeben
    setRoleLoading(false);
  }

  /**
   * Übernimmt eine neue Sitzung (Session) in den React-Zustand.
   *
   * @param {object|null} session - Das Supabase Session-Objekt oder null beim Abmelden
   */
  function applySession(session) {
    const nextUser = session?.user ?? null;

    // Abgemeldet: Alle Benutzerdaten zurücksetzen
    if (!nextUser) {
      setUser(null);
      setRole(null);
      setPermissions({ hasEinkauf: false });
      setRoleLoading(false);
      setInitialLoading(false);
      return;
    }

    setUser(prevUser => {
      // Identität bewahren: Bei automatischem Token-Refresh bleibt die Benutzer-ID gleich
      if (prevUser && prevUser.id === nextUser.id) {
        return prevUser;
      }
      // Neuer Benutzer: Rolle muss frisch aus der Datenbank geladen werden
      setRoleLoading(true);
      return nextUser;
    });
  }

  // Initiales Laden der bestehenden Session und Abonnieren von Authentifizierungsereignissen
  useEffect(() => {
    let mounted = true;

    // Session beim ersten Laden abfragen
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        applySession(session);
      })
      .catch(err => {
        console.error('Fehler beim Initialisieren der Session:', err);
        if (!mounted) return;
        setUser(null);
        setRole(null);
        setRoleLoading(false);
        setInitialLoading(false);
      });

    // Listener für Login/Logout Events und Hintergrund-Aktualisierungen.
    // WICHTIG: Der Callback ist synchron und fasst weder supabase.auth noch
    // die Datenbank an, um Lock-Blockaden zu verhindern.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      applySession(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Rolle und Berechtigungen laden, sobald ein Nutzer feststeht.
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    let stale = false;
    fetchRole(userId, () => stale);

    return () => {
      stale = true;
    };
  }, [user?.id]);

  /**
   * Meldet einen Benutzer mit Benutzername und Passwort an.
   * @param {string} username - Der Benutzername
   * @param {string} password - Das Passwort
   * @param {string} [captchaToken] - Optionales CAPTCHA-Token
   * @returns {Promise<object>} Ergebnis des Anmeldevorgangs
   */
  const login = async (username, password, captchaToken) => {
    return await loginWithUsername(username, password, captchaToken);
  };

  /**
   * Meldet den aktuellen Benutzer ab.
   * @returns {Promise<object>} Supabase SignOut-Ergebnis
   */
  const logout = async () => {
    return await supabase.auth.signOut();
  };

  // Automatischer Logout nach 10 Minuten Inaktivität
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_MS = 10 * 60 * 1000; // 10 Minuten
    const THROTTLE_MS = 30_000; // Nur alle 30s den Timer bei Mausbewegung zurücksetzen
    let timeoutId;
    let lastReset = Date.now();

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout();
      }, INACTIVITY_MS);
    };

    // Gedrosselte Version für hochfrequente Events (z. B. mousemove)
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset >= THROTTLE_MS) {
        lastReset = now;
        resetTimer();
      }
    };

    // Timer sofort initialisieren
    resetTimer();

    // Event-Listener für Benutzeraktivität registrieren
    window.addEventListener('mousemove', throttledReset);
    window.addEventListener('scroll', throttledReset);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('pointerdown', resetTimer);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', throttledReset);
      window.removeEventListener('scroll', throttledReset);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('pointerdown', resetTimer);
    };
  }, [user]);

  // Hinweis bei ungewöhnlich langer Verbindungsdauer
  useEffect(() => {
    if (!initialLoading) return;

    const warnTimer = setTimeout(() => {
      setLoadingTooLong(true);
    }, 2500);

    return () => {
      clearTimeout(warnTimer);
    };
  }, [initialLoading]);

  return (
    <AuthContext.Provider value={{ user, role, roleLoading, permissions, loading: initialLoading, login, logout }}>
      {!initialLoading ? (
        children
      ) : (
        <div className="global-loading-container" style={{ flexDirection: 'column', gap: '0.75rem', textAlign: 'center' }}>
          <span>{loadingTooLong ? 'Verbindung dauert ungewöhnlich lange…' : 'Lade App…'}</span>
          {loadingTooLong && (
            <button 
              type="button" 
              className="btn btn-outline btn-sm" 
              onClick={() => window.location.reload()}
              style={{ margin: '0 auto', fontSize: '0.85rem' }}
            >
              Seite neu laden
            </button>
          )}
        </div>
      )}
    </AuthContext.Provider>
  );
};

/**
 * Custom Hook für den einfachen Zugriff auf den AuthContext.
 * @returns {object} AuthContext-Methoden und Zustände
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  return useContext(AuthContext);
};
